import * as i0 from '@angular/core';
import { InjectionToken, Directive, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Pipe, Inject, HostBinding, ContentChildren, ContentChild, NgModule, ChangeDetectorRef, NgZone } from '@angular/core';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { ComponentPortal, DomPortalOutlet, PortalModule } from '@angular/cdk/portal';
import * as i1 from '@coyoapp/ui/loading';
import { LoadingModule } from '@coyoapp/ui/loading';
import * as i2$1 from '@coyoapp/ui/icon';
import { IconModule } from '@coyoapp/ui/icon';
import { EMPTY, merge, Subject } from 'rxjs';
import { startWith, map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { FocusMonitor } from '@angular/cdk/a11y';
import { WrappedFormControlBase, isNil } from '@coyoapp/ui/cdk';

const CUI_FORM_FIELD_DEFAULT_CONFIG = {
    i18n: {
        min: context => `This value must be bigger than ${context.min}.`,
        max: context => `This value must be smaller than ${context.max}.`,
        required: 'This is required.',
        requiredtrue: 'This must be true.',
        email: 'This must be a valid e-mail address.',
        minlength: context => `This field requires at least ${context.requiredLength} characters.`,
        maxlength: context => `This field must be shorter than ${context.requiredLength} characters.`,
        pattern: 'This field contains an invalid format.'
    }
};
const CUI_FORM_FIELD_CONFIG = new InjectionToken('CUI_FORM_FIELD_OPTIONS', {
    factory: () => CUI_FORM_FIELD_DEFAULT_CONFIG
});

const FORM_FIELD = new InjectionToken('FORM_FIELD');
/**
 * Use in the `providers` of a component that implements `FormFieldControl`
 * to reduce some boilerplate.
 *
 * @param type the type of the provided control
 * @returns the form field provider
 *
 * @example
 * ```ts
 * @Component({ providers: [provideFormFieldControl(MyFormControl)] }
 * class MyFormControl implements FormFieldControl {
 *   // ...
 * }
 * ```
 */
function provideFormFieldControl(type) {
    return {
        provide: FORM_FIELD,
        useExisting: type,
        multi: true
    };
}

const CUI_FORM_HINT = new InjectionToken('FormHint');
class FormHintDirective {
}
FormHintDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormHintDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FormHintDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormHintDirective, selector: "[cuiFormHint]", providers: [{ provide: CUI_FORM_HINT, useExisting: FormHintDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormHintDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cuiFormHint]',
                    providers: [{ provide: CUI_FORM_HINT, useExisting: FormHintDirective }]
                }]
        }] });

const CUI_FORM_INFO = new InjectionToken('FormInfo');
class FormInfoDirective {
}
FormInfoDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormInfoDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FormInfoDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormInfoDirective, selector: "[cuiFormInfo]", providers: [{ provide: CUI_FORM_INFO, useExisting: FormInfoDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormInfoDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cuiFormInfo]',
                    providers: [{ provide: CUI_FORM_INFO, useExisting: FormInfoDirective }]
                }]
        }] });

const CUI_FORM_PREFIX = new InjectionToken('FormPrefix');
class FormPrefixDirective {
}
FormPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FormPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormPrefixDirective, selector: "[cuiFormPrefix]", providers: [{ provide: CUI_FORM_PREFIX, useExisting: FormPrefixDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cuiFormPrefix]',
                    providers: [{ provide: CUI_FORM_PREFIX, useExisting: FormPrefixDirective }]
                }]
        }] });

const CUI_FORM_SUFFIX = new InjectionToken('FormSuffix');
class FormSuffixDirective {
}
FormSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FormSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormSuffixDirective, selector: "[cuiFormSuffix]", providers: [{ provide: CUI_FORM_SUFFIX, useExisting: FormSuffixDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cuiFormSuffix]',
                    providers: [{ provide: CUI_FORM_SUFFIX, useExisting: FormSuffixDirective }]
                }]
        }] });

class FormFieldLabelComponent {
}
FormFieldLabelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FormFieldLabelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: FormFieldLabelComponent, selector: "cui-form-field-label", inputs: { for: "for", form: "form" }, host: { classAttribute: "cui-form-field-label" }, ngImport: i0, template: "<label [attr.for]=\"for\" [attr.form]=\"form\"><ng-content></ng-content></label>\n", styles: [".cui-form-field-label{display:block;margin-bottom:.5rem}.cui-form-field-label label{font-size:.875rem;line-height:1rem;font-weight:600;color:#697687;transition:color .15s linear}.cui-input-info .cui-form-field-label{margin-bottom:0}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-form-field-label', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        class: 'cui-form-field-label'
                    }, template: "<label [attr.for]=\"for\" [attr.form]=\"form\"><ng-content></ng-content></label>\n", styles: [".cui-form-field-label{display:block;margin-bottom:.5rem}.cui-form-field-label label{font-size:.875rem;line-height:1rem;font-weight:600;color:#697687;transition:color .15s linear}.cui-input-info .cui-form-field-label{margin-bottom:0}\n"] }]
        }], propDecorators: { for: [{
                type: Input
            }], form: [{
                type: Input
            }] } });

class FormFieldErrorPipe {
    constructor(options) {
        this.options = options;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    transform(validator, context, errors) {
        const message = errors?.[validator] || this.options.i18n[validator];
        if (!message) {
            console.warn(`[FormFieldErrorPipe] Could not find message for "${validator}"`);
            return 'Error';
        }
        return message instanceof Function
            ? message(context)
            : message;
    }
}
FormFieldErrorPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldErrorPipe, deps: [{ token: CUI_FORM_FIELD_CONFIG }], target: i0.ɵɵFactoryTarget.Pipe });
FormFieldErrorPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldErrorPipe, name: "formFieldError" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldErrorPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'formFieldError'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [CUI_FORM_FIELD_CONFIG]
                }] }]; } });

class FormFieldComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        /**
         * The (auto-connected) label of the form field.
         */
        this.label = '';
        /**
         * The size of the form field.
         */
        this.size = 'm';
        /**
         * Wether to show an input count.
         */
        this.count = false;
        /**
         * The appearance mode of the form field.
         */
        this.mode = 'primary';
        this.controlNonStatic = null;
        this.controlStatic = null;
    }
    get touched() { return !!this.control?.ngControl?.touched; }
    get invalid() { return !!this.control?.ngControl?.invalid; }
    get cdkFocused() { return this.control?.focusOrigin != null; }
    get cdkTouchFocused() { return this.control?.focusOrigin === 'touch'; }
    get cdkMouseFocused() { return this.control?.focusOrigin === 'mouse'; }
    get cdkKeyboardFocused() { return this.control?.focusOrigin === 'keyboard'; }
    get cdkProgramFocused() { return this.control?.focusOrigin === 'program'; }
    get control() {
        return this.controlNonStatic || this.controlStatic;
    }
    get count$() {
        return (this.control?.ngControl?.valueChanges || EMPTY)
            .pipe(startWith(this.control?.ngControl?.value))
            .pipe(map(value => ('' + (value || '')).length))
            .pipe(distinctUntilChanged());
    }
    ngAfterContentInit() {
        merge(this.control?.touched$ || EMPTY, this.control?.ngControl?.statusChanges || EMPTY, this.infoChildren.changes, this.prefixChildren.changes, this.suffixChildren.changes, this.hintChildren.changes).subscribe(() => {
            this.changeDetectorRef.markForCheck();
        });
    }
    focus($event) {
        $event?.preventDefault();
        this.control?.focus('mouse', { preventScroll: true });
    }
}
FormFieldComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FormFieldComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: FormFieldComponent, selector: "cui-form-field", inputs: { label: "label", size: "size", count: "count", mode: "mode", errors: "errors" }, host: { properties: { "class.disabled": "control?.disabled", "attr.data-cui-size": "this.size", "attr.data-cui-mode": "this.mode", "class.cui-form-field-touched": "this.touched", "class.cui-form-field-invalid": "this.invalid", "class.cdk-focused": "this.cdkFocused", "class.cdk-touch-focused": "this.cdkTouchFocused", "class.cdk-mouse-focused": "this.cdkMouseFocused", "class.cdk-keyboard-focused": "this.cdkKeyboardFocused", "class.cdk-program-focused": "this.cdkProgramFocused" }, classAttribute: "cui-form-field" }, queries: [{ propertyName: "controlNonStatic", first: true, predicate: FORM_FIELD, descendants: true }, { propertyName: "controlStatic", first: true, predicate: FORM_FIELD, descendants: true, static: true }, { propertyName: "infoChildren", predicate: CUI_FORM_INFO, descendants: true }, { propertyName: "prefixChildren", predicate: CUI_FORM_PREFIX, descendants: true }, { propertyName: "suffixChildren", predicate: CUI_FORM_SUFFIX, descendants: true }, { propertyName: "hintChildren", predicate: CUI_FORM_HINT, descendants: true }], ngImport: i0, template: "<div class=\"cui-input-info\" *ngIf=\"label || count || infoChildren.length\">\n  <cui-form-field-label class=\"cui-mb0\" *ngIf=\"label\" [attr.for]=\"control?.id\" (click)=\"focus($event)\">{{label}}</cui-form-field-label>\n  <div class=\"cui-input-info-right\" *ngIf=\"count || infoChildren.length\">\n    <ng-content select=\"[cuiFormInfo]\"></ng-content>\n    <span *ngIf=\"count\" class=\"cui-input-count\" [class.cui-ml2]=\"infoChildren.length\">{{count$ | async}}<ng-container *ngIf=\"control?.maxlength\">/{{control?.maxlength}}</ng-container></span>\n  </div>\n</div>\n<div class=\"cui-input-wrapper\">\n  <div class=\"cui-input-prefix\" *ngIf=\"prefixChildren?.length\" (click)=\"focus()\">\n    <ng-content select=\"[cuiFormPrefix]\"></ng-content>\n  </div>\n  <ng-content></ng-content>\n  <div class=\"cui-input-suffix\" *ngIf=\"suffixChildren?.length\" (click)=\"focus()\">\n    <ng-content select=\"[cuiFormSuffix]\"></ng-content>\n  </div>\n</div>\n<div class=\"cui-input-hints\" *ngIf=\"hintChildren?.length || control?.ngControl?.touched && control?.ngControl?.invalid\">\n  <ng-template #hints>\n    <ng-content select=\"[cuiFormHint]\"></ng-content>\n  </ng-template>\n  <ng-container *ngIf=\"control?.ngControl?.touched && control?.ngControl?.invalid; else hints\">\n    <p class=\"cui-input-error\" *ngFor=\"let error of control?.ngControl?.errors | keyvalue\">{{error.key | formFieldError : error.value : errors}}</p>\n  </ng-container>\n</div>\n", styles: [".cui-input-hints{margin-top:.25rem;font-size:.875rem;line-height:1rem;font-weight:400;color:#697687;transition:color .15s linear}.cui-input-hints>*{margin:.25rem 0 0}.cui-form-field{display:flex;flex-direction:column;min-width:0}.cui-form-field.disabled{opacity:.65}.cui-form-field.disabled .cui-form-field-label,.cui-form-field.disabled .cui-input-wrapper{cursor:not-allowed}.cui-form-field.disabled .cui-form-field-label *,.cui-form-field.disabled .cui-input-wrapper *{pointer-events:none}.cui-form-field .cui-input-info{font-size:.875rem;line-height:1rem;font-weight:400;display:flex;justify-content:space-between;color:#697687;margin-bottom:.5rem;transition:color .15s linear}.cui-form-field .cui-input-info-right{display:flex;margin-left:.5rem}.cui-form-field .cui-input-count{white-space:nowrap}.cui-form-field .cui-input-wrapper{display:inline-flex;align-items:stretch;border-radius:var(--cui-border-radius-m, .25rem);transition:box-shadow .15s linear,background-color .15s linear}.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper{outline:1px solid #ff5531}.cui-form-field.cdk-keyboard-focused.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper,.cui-form-field.cdk-program-focused.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper{outline:2px solid #ff5531}.cui-form-field .cui-input-prefix,.cui-form-field .cui-input-suffix{display:inline-flex;align-items:center;color:var(--cui-input-prefix-color, #697687);padding-left:.5rem;padding-right:.5rem;-webkit-user-select:none;user-select:none;font-size:.9375rem;line-height:1.25rem;font-weight:400}.cui-form-field .cui-input-prefix{margin-right:-1rem}.cui-form-field .cui-input-suffix{margin-left:-1rem}.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-info,.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-hints{color:#dd350d}.cui-form-field.cdk-focused label{color:#101d30}.cui-form-field[data-cui-mode=primary] .cui-input-wrapper{outline:1px solid var(--cui-primary-input-border, #d7dbe0);background-color:var(--cui-primary-input-background, #ffffff)}.cui-form-field[data-cui-mode=primary]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-primary-input-border-hover, #a8b1ba);background-color:var(--cui-primary-input-background-hover, #ffffff)}.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-keyboard-focused .cui-input-wrapper,.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-program-focused .cui-input-wrapper{outline:2px solid var(--cui-focus-color, #73b9ff)}.cui-form-field[data-cui-mode=primary] input:-webkit-autofill,.cui-form-field[data-cui-mode=primary] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-primary-input-background, #ffffff) inset}.cui-form-field[data-cui-mode=secondary] .cui-input-wrapper{outline:1px solid var(--cui-secondary-input-border, transparent);background-color:var(--cui-secondary-input-background, #f2f4f7)}.cui-form-field[data-cui-mode=secondary]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=secondary]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-secondary-input-border-hover, transparent);background-color:var(--cui-secondary-input-background-hover, #ebecf0)}.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-secondary-input-background, #f2f4f7) inset}.cui-form-field[data-cui-mode=ghost] .cui-input-wrapper{outline:1px solid var(--cui-ghost-input-border, transparent);background-color:var(--cui-ghost-input-background, transparent)}.cui-form-field[data-cui-mode=ghost]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-ghost-input-border-hover, transparent);background-color:var(--cui-ghost-input-background-hover, transparent)}.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-keyboard-focused .cui-input-wrapper,.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-program-focused .cui-input-wrapper{outline:2px solid var(--cui-focus-color, #73b9ff)}.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-ghost-input-background, transparent) inset}form .cui-form-field{margin-bottom:1rem}\n"], components: [{ type: FormFieldLabelComponent, selector: "cui-form-field-label", inputs: ["for", "form"] }], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], pipes: { "async": i2.AsyncPipe, "keyvalue": i2.KeyValuePipe, "formFieldError": FormFieldErrorPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cui-form-field', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        'class': 'cui-form-field',
                        '[class.disabled]': 'control?.disabled'
                    }, template: "<div class=\"cui-input-info\" *ngIf=\"label || count || infoChildren.length\">\n  <cui-form-field-label class=\"cui-mb0\" *ngIf=\"label\" [attr.for]=\"control?.id\" (click)=\"focus($event)\">{{label}}</cui-form-field-label>\n  <div class=\"cui-input-info-right\" *ngIf=\"count || infoChildren.length\">\n    <ng-content select=\"[cuiFormInfo]\"></ng-content>\n    <span *ngIf=\"count\" class=\"cui-input-count\" [class.cui-ml2]=\"infoChildren.length\">{{count$ | async}}<ng-container *ngIf=\"control?.maxlength\">/{{control?.maxlength}}</ng-container></span>\n  </div>\n</div>\n<div class=\"cui-input-wrapper\">\n  <div class=\"cui-input-prefix\" *ngIf=\"prefixChildren?.length\" (click)=\"focus()\">\n    <ng-content select=\"[cuiFormPrefix]\"></ng-content>\n  </div>\n  <ng-content></ng-content>\n  <div class=\"cui-input-suffix\" *ngIf=\"suffixChildren?.length\" (click)=\"focus()\">\n    <ng-content select=\"[cuiFormSuffix]\"></ng-content>\n  </div>\n</div>\n<div class=\"cui-input-hints\" *ngIf=\"hintChildren?.length || control?.ngControl?.touched && control?.ngControl?.invalid\">\n  <ng-template #hints>\n    <ng-content select=\"[cuiFormHint]\"></ng-content>\n  </ng-template>\n  <ng-container *ngIf=\"control?.ngControl?.touched && control?.ngControl?.invalid; else hints\">\n    <p class=\"cui-input-error\" *ngFor=\"let error of control?.ngControl?.errors | keyvalue\">{{error.key | formFieldError : error.value : errors}}</p>\n  </ng-container>\n</div>\n", styles: [".cui-input-hints{margin-top:.25rem;font-size:.875rem;line-height:1rem;font-weight:400;color:#697687;transition:color .15s linear}.cui-input-hints>*{margin:.25rem 0 0}.cui-form-field{display:flex;flex-direction:column;min-width:0}.cui-form-field.disabled{opacity:.65}.cui-form-field.disabled .cui-form-field-label,.cui-form-field.disabled .cui-input-wrapper{cursor:not-allowed}.cui-form-field.disabled .cui-form-field-label *,.cui-form-field.disabled .cui-input-wrapper *{pointer-events:none}.cui-form-field .cui-input-info{font-size:.875rem;line-height:1rem;font-weight:400;display:flex;justify-content:space-between;color:#697687;margin-bottom:.5rem;transition:color .15s linear}.cui-form-field .cui-input-info-right{display:flex;margin-left:.5rem}.cui-form-field .cui-input-count{white-space:nowrap}.cui-form-field .cui-input-wrapper{display:inline-flex;align-items:stretch;border-radius:var(--cui-border-radius-m, .25rem);transition:box-shadow .15s linear,background-color .15s linear}.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper{outline:1px solid #ff5531}.cui-form-field.cdk-keyboard-focused.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper,.cui-form-field.cdk-program-focused.cui-form-field-touched.cui-form-field-invalid .cui-input-wrapper{outline:2px solid #ff5531}.cui-form-field .cui-input-prefix,.cui-form-field .cui-input-suffix{display:inline-flex;align-items:center;color:var(--cui-input-prefix-color, #697687);padding-left:.5rem;padding-right:.5rem;-webkit-user-select:none;user-select:none;font-size:.9375rem;line-height:1.25rem;font-weight:400}.cui-form-field .cui-input-prefix{margin-right:-1rem}.cui-form-field .cui-input-suffix{margin-left:-1rem}.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-info,.cui-form-field.cui-form-field-touched.cui-form-field-invalid .cui-input-hints{color:#dd350d}.cui-form-field.cdk-focused label{color:#101d30}.cui-form-field[data-cui-mode=primary] .cui-input-wrapper{outline:1px solid var(--cui-primary-input-border, #d7dbe0);background-color:var(--cui-primary-input-background, #ffffff)}.cui-form-field[data-cui-mode=primary]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-primary-input-border-hover, #a8b1ba);background-color:var(--cui-primary-input-background-hover, #ffffff)}.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-keyboard-focused .cui-input-wrapper,.cui-form-field[data-cui-mode=primary]:not(.disabled).cdk-program-focused .cui-input-wrapper{outline:2px solid var(--cui-focus-color, #73b9ff)}.cui-form-field[data-cui-mode=primary] input:-webkit-autofill,.cui-form-field[data-cui-mode=primary] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=primary] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-primary-input-background, #ffffff) inset}.cui-form-field[data-cui-mode=secondary] .cui-input-wrapper{outline:1px solid var(--cui-secondary-input-border, transparent);background-color:var(--cui-secondary-input-background, #f2f4f7)}.cui-form-field[data-cui-mode=secondary]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=secondary]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-secondary-input-border-hover, transparent);background-color:var(--cui-secondary-input-background-hover, #ebecf0)}.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=secondary] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-secondary-input-background, #f2f4f7) inset}.cui-form-field[data-cui-mode=ghost] .cui-input-wrapper{outline:1px solid var(--cui-ghost-input-border, transparent);background-color:var(--cui-ghost-input-background, transparent)}.cui-form-field[data-cui-mode=ghost]:not(.disabled) .cui-input-wrapper:hover,.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-focused .cui-input-wrapper{outline:1px solid var(--cui-ghost-input-border-hover, transparent);background-color:var(--cui-ghost-input-background-hover, transparent)}.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-keyboard-focused .cui-input-wrapper,.cui-form-field[data-cui-mode=ghost]:not(.disabled).cdk-program-focused .cui-input-wrapper{outline:2px solid var(--cui-focus-color, #73b9ff)}.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] input:-webkit-autofill:focus,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] textarea:-webkit-autofill:focus,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill:hover,.cui-form-field[data-cui-mode=ghost] select:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 10rem var(--cui-ghost-input-background, transparent) inset}form .cui-form-field{margin-bottom:1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { label: [{
                type: Input
            }], size: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.data-cui-size']
            }], count: [{
                type: Input
            }], mode: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.data-cui-mode']
            }], errors: [{
                type: Input
            }], infoChildren: [{
                type: ContentChildren,
                args: [CUI_FORM_INFO, { descendants: true }]
            }], prefixChildren: [{
                type: ContentChildren,
                args: [CUI_FORM_PREFIX, { descendants: true }]
            }], suffixChildren: [{
                type: ContentChildren,
                args: [CUI_FORM_SUFFIX, { descendants: true }]
            }], hintChildren: [{
                type: ContentChildren,
                args: [CUI_FORM_HINT, { descendants: true }]
            }], touched: [{
                type: HostBinding,
                args: ['class.cui-form-field-touched']
            }], invalid: [{
                type: HostBinding,
                args: ['class.cui-form-field-invalid']
            }], cdkFocused: [{
                type: HostBinding,
                args: ['class.cdk-focused']
            }], cdkTouchFocused: [{
                type: HostBinding,
                args: ['class.cdk-touch-focused']
            }], cdkMouseFocused: [{
                type: HostBinding,
                args: ['class.cdk-mouse-focused']
            }], cdkKeyboardFocused: [{
                type: HostBinding,
                args: ['class.cdk-keyboard-focused']
            }], cdkProgramFocused: [{
                type: HostBinding,
                args: ['class.cdk-program-focused']
            }], controlNonStatic: [{
                type: ContentChild,
                args: [FORM_FIELD]
            }], controlStatic: [{
                type: ContentChild,
                args: [FORM_FIELD, { static: true }]
            }] } });

/**
 * COYO-UI SelfSave Directive
 *
 * @description
 * This directive allows the form field control to auto save on value change, given an input save function
 * that returns an Observable monitors the save backend call and returns the result.
 *
 * Directive Inputs
 * saveFn - the save callback function that has to return an observable
 * saveDebounceTime - debounce time before saving new value
 * successIcon - Icon to show on successful save
 * errorIcon - Icon to show when there is an error
 *
 * @example
 * <cui-form-field
 *   label="Self Saving Number Field"
 *   cuiFormSelfSave
 *   [successIcon]="'chevron-left'"
 *   [errorIcon]="'chevron-right'"
 *   [saveDebounceTime]="3000"
 *   [saveFn]="backendSaveSimulation('number')">
 *     <cui-input-number [formControl]="selfSavingControl"></cui-input-number>
 * </cui-form-field>
 */
const CUI_FORM_SELF_SAVE = new InjectionToken('FormSelfSave');
class FormSelfSaveDirective {
    constructor(elRef, componentFactoryResolver, injector, appRef, document) {
        this.elRef = elRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.appRef = appRef;
        this.document = document;
        /**
         * Debounce time before calling save function
         */
        this.saveDebounceTime = 1000;
        /**
         * Success Icon
         */
        this.successIcon = 'check';
        /**
         * Error Icon
         */
        this.errorIcon = 'close';
        this.valueChangedSubject = new Subject();
        this.componentPortalSuccess = new ComponentPortal(PortalSuccessComponent);
        this.componentPortalLoading = new ComponentPortal(PortalLoadingComponent);
        this.componentPortalError = new ComponentPortal(PortalErrorComponent);
    }
    ngOnInit() {
        // subscribe to value changes coming from the form control inside the form field component
        this.valueChangedSubject.asObservable()
            .pipe(debounceTime(this.saveDebounceTime))
            .pipe(distinctUntilChanged())
            .subscribe(() => {
            if (this.saveFn) {
                this.addLoader();
                const changeObservable = this.saveFn();
                this.changeValueSubscription = changeObservable.subscribe(() => this.handleSuccessCase(), () => this.handleErrorCase());
            }
        });
    }
    ngAfterViewInit() {
        this.inputElement = this.inputElement ?? this.elRef.nativeElement.querySelector('input') ?? this.elRef.nativeElement.querySelector('textarea');
        if (this.inputElement) {
            const inputID = this.inputElement.id;
            // register on change event on the input field
            this.inputElement.addEventListener('change', (event) => {
                this.inputChanged(event);
            });
            // get portal element from within form-field based on ID
            const portalElement = this.document.querySelector(`#portal-${inputID}`);
            // create portal host
            if (!this.portalHost && portalElement) {
                this.portalHost = new DomPortalOutlet(portalElement, this.componentFactoryResolver, this.appRef, this.injector);
            }
        }
    }
    inputChanged(event) {
        if (event?.target) {
            const inputValue = event.target.value;
            if (inputValue !== this.inputValue) {
                this.inputValue = inputValue;
                this.valueChangedSubject.next(this.inputValue);
            }
        }
    }
    ngOnDestroy() {
        this.removeAll();
        this.changeValueSubscription?.unsubscribe();
        this.inputElement?.removeEventListener('change', (event) => {
            this.inputChanged(event);
        });
    }
    /**
     * On successful save show the success component inside the portal
     */
    handleSuccessCase() {
        this.addSuccess();
        setTimeout(() => {
            this.removeAll();
        }, 1000);
    }
    /**
     * Show the loading animation
     */
    addLoader() {
        this.removeAll();
        if (this.componentPortalLoading) {
            this.portalHost?.attach(this.componentPortalLoading);
        }
    }
    /**
     * Adds the success component to the portal
     */
    addSuccess() {
        this.removeAll();
        if (this.componentPortalSuccess) {
            const componentRef = this.portalHost?.attach(this.componentPortalSuccess);
            componentRef.instance.iconName = this.successIcon;
        }
    }
    /**
     * Removes the attached components from the portal
     */
    removeAll() {
        this.portalHost?.detach();
    }
    /**
     * Show the error component on error
     */
    handleErrorCase() {
        this.removeAll();
        if (this.componentPortalError) {
            const componentRef = this.portalHost?.attach(this.componentPortalError);
            componentRef.instance.iconName = this.errorIcon;
        }
        setTimeout(() => {
            this.removeAll();
        }, 1000);
    }
}
FormSelfSaveDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormSelfSaveDirective, deps: [{ token: i0.ElementRef }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive });
FormSelfSaveDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormSelfSaveDirective, selector: "[cuiFormSelfSave]", inputs: { saveFn: "saveFn", saveDebounceTime: "saveDebounceTime", successIcon: "successIcon", errorIcon: "errorIcon" }, providers: [{ provide: CUI_FORM_SELF_SAVE, useExisting: FormSelfSaveDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormSelfSaveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cuiFormSelfSave]',
                    providers: [{ provide: CUI_FORM_SELF_SAVE, useExisting: FormSelfSaveDirective }]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { saveFn: [{
                type: Input
            }], saveDebounceTime: [{
                type: Input
            }], successIcon: [{
                type: Input
            }], errorIcon: [{
                type: Input
            }] } });
class PortalLoadingComponent {
}
PortalLoadingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalLoadingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PortalLoadingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: PortalLoadingComponent, selector: "cui-loading-component-portal", ngImport: i0, template: '<cui-spinner class="cui-form-portal-loading" size="s"></cui-spinner>', isInline: true, components: [{ type: i1.SpinnerComponent, selector: "cui-spinner", inputs: ["size"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalLoadingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cui-loading-component-portal',
                    template: '<cui-spinner class="cui-form-portal-loading" size="s"></cui-spinner>'
                }]
        }] });
class PortalSuccessComponent {
    constructor() {
        this.iconName = 'check';
    }
}
PortalSuccessComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalSuccessComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PortalSuccessComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: PortalSuccessComponent, selector: "cui-success-component-portal", inputs: { iconName: "iconName" }, ngImport: i0, template: '<cui-icon class="cui-form-portal-loading" [name]="iconName"></cui-icon>', isInline: true, components: [{ type: i2$1.IconComponent, selector: "cui-icon", inputs: ["name", "size"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalSuccessComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cui-success-component-portal',
                    template: '<cui-icon class="cui-form-portal-loading" [name]="iconName"></cui-icon>'
                }]
        }], propDecorators: { iconName: [{
                type: Input
            }] } });
class PortalErrorComponent {
    constructor() {
        this.iconName = 'close';
    }
}
PortalErrorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalErrorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PortalErrorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.1", type: PortalErrorComponent, selector: "cui-error-component-portal", inputs: { iconName: "iconName" }, ngImport: i0, template: '<cui-icon class="cui-form-portal-error" [name]="iconName"></cui-icon>', isInline: true, components: [{ type: i2$1.IconComponent, selector: "cui-icon", inputs: ["name", "size"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: PortalErrorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cui-error-component-portal',
                    template: '<cui-icon class="cui-form-portal-error" [name]="iconName"></cui-icon>'
                }]
        }], propDecorators: { iconName: [{
                type: Input
            }] } });

class FormFieldModule {
}
FormFieldModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FormFieldModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldModule, declarations: [FormFieldComponent,
        FormHintDirective,
        FormPrefixDirective,
        FormSuffixDirective,
        FormInfoDirective,
        FormFieldErrorPipe,
        FormFieldLabelComponent,
        FormSelfSaveDirective,
        PortalLoadingComponent,
        PortalSuccessComponent,
        PortalErrorComponent], imports: [CommonModule,
        PortalModule,
        LoadingModule,
        IconModule], exports: [FormFieldComponent,
        FormHintDirective,
        FormPrefixDirective,
        FormSuffixDirective,
        FormInfoDirective,
        FormFieldLabelComponent,
        FormSelfSaveDirective] });
FormFieldModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldModule, imports: [[
            CommonModule,
            PortalModule,
            LoadingModule,
            IconModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        PortalModule,
                        LoadingModule,
                        IconModule
                    ],
                    declarations: [
                        FormFieldComponent,
                        FormHintDirective,
                        FormPrefixDirective,
                        FormSuffixDirective,
                        FormInfoDirective,
                        FormFieldErrorPipe,
                        FormFieldLabelComponent,
                        FormSelfSaveDirective,
                        PortalLoadingComponent,
                        PortalSuccessComponent,
                        PortalErrorComponent
                    ],
                    exports: [
                        FormFieldComponent,
                        FormHintDirective,
                        FormPrefixDirective,
                        FormSuffixDirective,
                        FormInfoDirective,
                        FormFieldLabelComponent,
                        FormSelfSaveDirective
                    ]
                }]
        }] });

/* eslint-disable @angular-eslint/directive-class-suffix */
let nextUniqueId = 0;
class FormFieldControlComponent extends WrappedFormControlBase {
    constructor(injector) {
        super(injector);
        this._uid = `cui-control-${nextUniqueId++}`;
        this._id = this._uid;
        /**
         * Whether this form element is required.
         */
        this.required = false;
        /**
         * Specifies a short hint that describes the expected value of this form element.
         */
        this.placeholder = '';
        this._focusOrigin = null;
        /**
         * Unfortunately, there is no way to retrieve a list of registered validators
         * for a form field. Thus, we need to use this workaround to retrieve the max.
         * length defined for the field. A corresponding feature request is tracked at
         * https://github.com/angular/angular/issues/13461.
         */
        this.maxlength = null;
        this.autofilled = false;
        this.cdRef = injector.get(ChangeDetectorRef);
        this.ngZone = injector.get(NgZone);
        this.focusMonitor = injector.get(FocusMonitor);
    }
    /**
     * The ID of this form element.
     */
    get id() { return this._id; }
    set id(value) { this._id = value || this._uid; }
    /**
     * The current value of this form element.
     */
    get value() { return this.formControl.value; }
    set value(value) { this.formControl.setValue(value); }
    /**
     * Whether this form element has a value.
     */
    get hasValue() {
        return !isNil(this.value);
    }
    /**
     * Whether this form element is disabled.
     */
    get disabled() {
        return this.formControl.disabled;
    }
    /**
     * The focus origin of the native HTML element.
     *
     * @see https://material.angular.io/cdk/a11y/overview#focusmonitor
     */
    get focusOrigin() { return this._focusOrigin; }
    ngAfterViewInit() {
        if (this.focusElementRef) {
            this.focusMonitor.monitor(this.focusElementRef)
                .subscribe(origin => this.ngZone.run(() => {
                this._focusOrigin = origin;
                this.cdRef.markForCheck();
            }));
        }
    }
    ngOnDestroy() {
        if (this.focusElementRef) {
            this.focusMonitor.stopMonitoring(this.focusElementRef);
        }
    }
    /**
     * Focus the native form element.
     *
     * @param origin Focus origin.
     * @param options Options that can be used to configure the focus behavior.
     */
    focus(origin = 'program', options) {
        if (this.focusElementRef) {
            this.focusMonitor.focusVia(this.focusElementRef.nativeElement, origin, options);
        }
    }
    /**
     * Clears the form field.
     */
    clear() {
        this.value = null;
    }
}
FormFieldControlComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldControlComponent, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive });
FormFieldControlComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.1", type: FormFieldControlComponent, inputs: { id: "id", required: "required", placeholder: "placeholder", autocomplete: "autocomplete", tabIndex: "tabIndex" }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.1", ngImport: i0, type: FormFieldControlComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.Injector }]; }, propDecorators: { id: [{
                type: Input
            }], required: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { CUI_FORM_FIELD_CONFIG, CUI_FORM_FIELD_DEFAULT_CONFIG, CUI_FORM_HINT, CUI_FORM_INFO, CUI_FORM_PREFIX, CUI_FORM_SELF_SAVE, CUI_FORM_SUFFIX, FORM_FIELD, FormFieldComponent, FormFieldControlComponent, FormFieldLabelComponent, FormFieldModule, FormHintDirective, FormInfoDirective, FormPrefixDirective, FormSelfSaveDirective, FormSuffixDirective, PortalErrorComponent, PortalLoadingComponent, PortalSuccessComponent, provideFormFieldControl };
